import { Button, Dropdown, Badge } from "antd";
import { LogOut, User, Bell, Mail, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Link } from "react-router";
import { useSidebar } from "~/providers/sidebar.provider";
import { useAuth } from "~/store";
import { notificationsAPI } from "~/services/notifications/notifications.api";

export const Header: React.FC = () => {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const { user, logout } = useAuth();
  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();

  const handleToggle = () => {
    if (window.innerWidth >= 991) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch unread notification count
  useEffect(() => {
    if (!user?.id) return;

    const fetchUnreadCount = async () => {
      try {
        const count = await notificationsAPI.getUnreadCount(user.id);
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();

    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <header className="sticky top-0 flex w-full bg-white border-gray-300 z-20 lg:border-b backdrop-blur-md shadow-sm">
      <div className="flex flex-col items-center justify-between grow lg:flex-row lg:px-6">
        <div className="flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-800 sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-4">
          <button
            className="cursor-pointer items-center justify-center w-10 h-10 text-gray-500 border-gray-200 rounded-lg dark:border-gray-800 lg:flex dark:text-gray-400 lg:h-11 lg:w-11 lg:border"
            onClick={handleToggle}
            aria-label="Toggle Sidebar"
          >
            {isMobileOpen ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {/* Cross Icon */}
          </button>

          <Link to="/" className="lg:hidden">
            <img
              className="size-5 dark:hidden"
              src="/logo.png"
              alt="Logo"
            />
            <img
              className="size-5 hidden dark:block"
              src="/logo.png"
              alt="Logo"
            />
          </Link>

          <button
            onClick={toggleApplicationMenu}
            className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                fill="currentColor"
              />
            </svg>
          </button>

        </div>
        <div
          className={`${
            isApplicationMenuOpen ? "flex" : "hidden"
          } items-center justify-between w-full gap-4 px-5 py-4 lg:flex shadow-theme-md lg:justify-end lg:px-0 lg:shadow-none`}
        >
          {/* Notification Bell with Contact Dropdown */}
          <Dropdown
            menu={{
              items: [
                {
                  key: "notifications",
                  label: (
                    <Link to="/notifications" className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900">
                      <Bell size={16} />
                      <span>View Notifications</span>
                      {unreadCount > 0 && (
                        <Badge count={unreadCount} size="small" />
                      )}
                    </Link>
                  ),
                },
                { type: "divider" },
                {
                  key: "contact",
                  label: (
                    <div className="px-3 py-2">
                      <span className="font-bold text-sm">Contact Us</span>
                    </div>
                  ),
                  disabled: true,
                },
                { type: "divider" },
                {
                  key: "email",
                  label: (
                    <a
                      href="mailto:info@arthandlercollective.org"
                      className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900"
                    >
                      <Mail size={16} />
                      <span>info@arthandlercollective.org</span>
                    </a>
                  ),
                },
                {
                  key: "phone",
                  label: (
                    <a
                      href="tel:646-741-4047"
                      className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900"
                    >
                      <Phone size={16} />
                      <span>646-741-4047</span>
                    </a>
                  ),
                },
              ],
            }}
            trigger={["click"]}
          >
            <Badge count={unreadCount} offset={[-2, 2]}>
              <Button
                type="text"
                className="items-center text-gray-600 flex"
                icon={<Bell size={18} />}
              />
            </Badge>
          </Dropdown>

          {/* <UserDropdown /> */}
          <Dropdown
            menu={{
              items: [
                {
                  key: "1",
                  label: "My Account",
                  disabled: true,
                  className: "px-3 py-2",
                },
                { type: "divider" },
                {
                  key: "profile",
                  label: "Profile",
                  icon: <User size={16} />,
                  className: "px-3 py-2",
                },

                {
                  key: "logout",
                  label: "Logout",
                  icon: <LogOut size={16} />,
                  className: "px-3 py-2",
                  onClick: logout,
                },
              ],
            }}
          >
            <Button
              type="text"
              className="items-center text-gray-600 hidden md:flex"
              icon={<User />}
            >
              {user?.username}
            </Button>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
